import { CodeList } from '@common/classes/codelist';
import { TranslateService } from '@ngx-translate/core';
import { PropertyFunction } from './util.interface';

export function resolveProperty<T>(property: T | PropertyFunction<T>): T {
    return typeof property === 'function' ? (property as PropertyFunction<T>)() : property;
}

/**
 * Utility function to assign a type to an object.
 * It's usefull in templates when Angular can't infer the type of an object.
 * @param obj object to convert to type T
 * @returns obj typed as T
 */
export function toType<T>(obj: any): T {
    return obj as T;
}

export const defaultSelectLabel = (item: CodeList): string =>
    `${item.customText || (item.localizedCode || item.code || item.id) + (item.name ? ` - ${item.name}` : '')}`;

// return months for dropdown
export const MONTHS = (t: TranslateService): { value: number; label: string }[] => {
    const monthKeys = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    return monthKeys.map((key, index) => ({
        value: index + 1,
        label: t.instant(key)
    }));
};

// fake regions codelist
export const REGIONS: { value: string; label: string }[] = [
    { value: '31', label: '31 - VLASTITI PRIHODI' },
    { value: '43', label: '43 - OSTALI PRIHODI ZA POSEBNE NAMJENE' }
];

// return years for dropdown
export function getYears(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 30 }, (_, i) => currentYear - 20 + i);
}

export function getStringObjectYears(): { value: number; label: string }[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 30 }, (_, i) => {
        const year = currentYear - 20 + i;
        return { value: year, label: year.toString() };
    });
}

/**
 * Compares two objects for shallow equality by checking if all properties have the same values
 * @param x - First object to compare
 * @param y - Second object to compare
 * @returns true if objects have the same properties with the same values, false otherwise
 */
export function objectsAreSame(x: any, y: any): boolean {
    // Handle null/undefined cases
    if (x === y) return true;
    if (x == null || y == null) return false;
    if (typeof x !== 'object' || typeof y !== 'object') return x === y;

    // Check if both have the same number of properties
    const xKeys = Object.keys(x);
    const yKeys = Object.keys(y);
    if (xKeys.length !== yKeys.length) return false;

    // Check each property
    for (const propertyName in x) {
        if (x[propertyName] !== y[propertyName]) {
            return false;
        }
    }
    return true;
}

/**
 * Compares two arrays for equality by checking if all elements are the same objects
 * @param arr1 - First array to compare
 * @param arr2 - Second array to compare
 * @returns true if arrays have the same length and all corresponding elements are equal objects
 */
export function arraysAreSame(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
        if (!objectsAreSame(arr1[i], arr2[i])) {
            return false;
        }
    }
    return true;
}
